import { FunctionComponent, MouseEvent, createElement } from 'react';
import cn from 'classnames';

export enum TagVariants {
  IndigoGradient = 'bg-gradient-to-r from-indigo-600 via-pink-500 to-yellow-500 text-white',
  Yellow = 'bg-yellow-100 text-yellow-600',
  Red = 'bg-rose-200 text-rose-900',
  Gray = 'bg-gray-100 text-gray-600',
  Teal = 'bg-teal-400 text-teal-700',
  DarkTeal = 'bg-teal-800 text-white',
  Sage = 'bg-sage-400 text-sage-700',
  Violet = 'bg-violet-100 text-violet-700',
  Lime = 'bg-lime-500 text-white',
}

export enum MaxSize {
  Medium = 'max-w-[12rem]',
}

export enum HelpTextPosition {
  Default = 'absolute bottom-0 flex flex-col items-center mb-8 w-48',
  TopAlignRight = 'absolute bottom-[calc(100%+4px)] right-0',
}

export enum CategoryTagColor {
  Blue = 'bg-blue-50 text-blue-600',
}

type CommonProps = {
  text: string;
  icon?: any;
  className?: string;
};

type TagProps = {
  variant: TagVariants;
  helpText?: string;
  maxSize?: MaxSize;
  helpTextPosition?: HelpTextPosition;
} & CommonProps;

export const Tag: FunctionComponent<TagProps> = ({
  text,
  icon,
  className,
  variant,
  helpText,
  maxSize,
  helpTextPosition = HelpTextPosition.Default,
}) => {
  const textClasses = maxSize
    ? 'text-ellipsis whitespace-nowrap overflow-hidden'
    : '';
  return (
    <div
      className={cn(
        'center group relative flex items-center self-center rounded-full px-3 py-1 text-xs font-bold',
        variant,
        className,
        maxSize
      )}
    >
      {icon && (
        <span
          aria-label="Tag icon"
          className={cn('mr-1 size-4 shrink-0 opacity-60')}
        >
          {createElement(icon)}
        </span>
      )}
      {helpText ? (
        <>
          <div className={helpTextPosition}>
            <span className="whitespace-no-wrap relative z-10 hidden rounded bg-black p-2 text-xs leading-none text-white shadow-lg group-hover:flex">
              {helpText}
            </span>
          </div>
          <p className={textClasses}>{text}</p>
        </>
      ) : (
        <p className={textClasses}>{text}</p>
      )}
    </div>
  );
};

type CategoryTagProps = {
  altText?: string;
  onClick?: (event: MouseEvent) => void;
} & CommonProps;

export const CategoryTag: FunctionComponent<CategoryTagProps> = ({
  text,
  altText,
  icon,
  className,
  onClick,
}) => {
  return (
    <div
      title={altText}
      className={cn(
        'mr-2 flex w-max items-center gap-x-2 rounded-lg border-1 border-gray-300 bg-white px-3 py-1 text-xs font-medium',
        className
      )}
      onClick={onClick}
    >
      {icon && (
        <span
          aria-label="Category tag icon"
          className="size-5 shrink-0 text-zinc-600"
        >
          {createElement(icon)}
        </span>
      )}
      <span className="uppercase text-gray-900">{text}</span>
    </div>
  );
};
